import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Building2,
  MessageCircle, UserPlus, Star, RefreshCw, Wrench, AlertCircle, X, Check,
} from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { customersApi, serviceCallsApi, complainsApi, ContactLookupHit } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import {
  isCallLogAvailable, getDeviceCallLog, normalizeNumber, displayNumber, CALL_TYPE, DeviceCall,
} from '../services/callLog';

type TileFilter = 'all' | 'in' | 'out' | 'missed';

/** Matches the options on the Service Calls page so the two stay in step. */
const SERVICE_TYPES = ['Cloud', 'Tally', 'TDL', 'Web/App'] as const;

/**
 * Log a service call or a complaint straight from a call's summary.
 *
 * Both are raised against the number that was actually on the phone, so the
 * common case — someone rings about a problem and it needs recording — takes a
 * couple of taps instead of a trip through Service Calls hunting for them again.
 */
const LogSheet: React.FC<{
  kind: 'service' | 'complain';
  number: string;
  person: string | null;
  customerId: number | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ kind, number, person, customerId, onClose, onDone }) => {
  const { showError } = useToast();
  const [serviceType, setServiceType] = useState<string>('');
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);

  const isService = kind === 'service';

  const save = async () => {
    if (!remark.trim()) {
      showError('Details required', isService ? 'Describe the service needed.' : 'Describe the complaint.');
      return;
    }
    setSaving(true);
    try {
      if (isService) {
        await serviceCallsApi.create({
          mobile_no: number,
          contact_person: person || undefined,
          service_type: serviceType || undefined,
          remark: remark.trim(),
          customer_id: customerId ?? undefined,
        });
      } else {
        await complainsApi.create({ phone_no: number, remark: remark.trim() });
      }
      onDone();
    } catch (e: any) {
      showError(isService ? 'Could not log service call' : 'Could not log complaint',
        e?.message || 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  return (
    // Flex column with a pinned footer: the sheet is capped at 85vh, so with a
    // select, a textarea and the on-screen keyboard up, a button that merely
    // sits at the end of the content scrolls out of reach. The footer stays put.
    <div className="fixed inset-0 z-[80] flex items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full bg-white rounded-t-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0 bg-white">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">
              {isService ? 'New service call' : 'New complaint'}
            </h3>
            <p className="text-xs text-gray-400 truncate">
              {person ? `${person} · ${number}` : number}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 shrink-0" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isService && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Service type</label>
              <select
                value={serviceType}
                onChange={e => setServiceType(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">Not specified</option>
                {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {isService ? 'What is needed' : 'Complaint'}
            </label>
            {/* No autoFocus: opening the keyboard the instant the sheet appears
                hides most of it before the user has seen what they are filling in. */}
            <textarea
              value={remark}
              onChange={e => setRemark(e.target.value)}
              rows={3}
              placeholder={isService ? 'Describe the service required' : 'Describe the complaint'}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
            />
          </div>

          {!customerId && (
            <p className="text-[11px] text-amber-600">
              This number is not linked to a customer, so it will be logged against the number alone.
            </p>
          )}
        </div>

        <div className="shrink-0 p-3 border-t border-gray-100 bg-white">
          <button
            onClick={save}
            disabled={saving || !remark.trim()}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
              isService ? 'bg-blue-600' : 'bg-amber-600'
            }`}
          >
            <Check className="h-4 w-4" />
            {saving ? 'Saving...' : isService ? 'Log service call' : 'Log complaint'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Every company this number is mapped to — the primary hit plus any others. */
function mappedCompanies(hit: ContactLookupHit | null) {
  if (!hit) return [];
  const all = [
    { customer_id: hit.customer_id, company: hit.company, person: hit.contact_person, primary: hit.primary_contact },
    ...(hit.others || []).map(o => ({
      customer_id: o.customer_id, company: o.company, person: o.contact_person, primary: o.primary_contact,
    })),
  ];
  // One button per customer, even if several contact rows point at the same one.
  const seen: Record<number, boolean> = {};
  return all.filter(c => {
    if (!c.customer_id || seen[c.customer_id]) return false;
    seen[c.customer_id] = true;
    return true;
  });
}

function callIcon(type: number, className = 'h-4 w-4') {
  switch (type) {
    case CALL_TYPE.INCOMING: return <PhoneIncoming className={`${className} text-emerald-600`} />;
    case CALL_TYPE.OUTGOING: return <PhoneOutgoing className={`${className} text-blue-600`} />;
    case CALL_TYPE.MISSED: return <PhoneMissed className={`${className} text-red-500`} />;
    case CALL_TYPE.REJECTED:
    case CALL_TYPE.BLOCKED: return <PhoneMissed className={`${className} text-gray-400`} />;
    default: return <Phone className={`${className} text-gray-400`} />;
  }
}

function fmtDuration(seconds: number) {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtWhen(ms: number) {
  const d = new Date(ms);
  return d.toLocaleString([], {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const CallDetail: React.FC = () => {
  const { number = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();

  // The list page hands the already-read calls over so this opens instantly.
  // On a direct navigation or a refresh there is no state, so fall back to
  // reading the device — the screen must work from a cold start too.
  const seeded = (location.state as any)?.calls as DeviceCall[] | undefined;

  const [calls, setCalls] = useState<DeviceCall[]>(seeded || []);
  const [hit, setHit] = useState<ContactLookupHit | null>(null);
  const [loading, setLoading] = useState(!seeded);
  const [tileFilter, setTileFilter] = useState<TileFilter>('all');
  const [logKind, setLogKind] = useState<'service' | 'complain' | null>(null);

  const key = normalizeNumber(number);

  const loadCalls = useCallback(async () => {
    if (!isCallLogAvailable) return;
    setLoading(true);
    try {
      // No date bound: this screen is explicitly "everything for this number".
      const rows = await getDeviceCallLog(10000, 0);
      setCalls(rows.filter(c => normalizeNumber(c.number) === key));
    } catch (e: any) {
      showError('Could not read call log', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [key, showError]);

  useEffect(() => {
    if (!seeded) loadCalls();
    // Seeded data is a snapshot from the list; refreshing is an explicit action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!key) return;
    customersApi.lookupContacts([key])
      .then(res => setHit((res.data || {})[key] || null))
      .catch(() => setHit(null));
  }, [key]);

  const stats = useMemo(() => {
    let incoming = 0, outgoing = 0, missed = 0, duration = 0;
    for (const c of calls) {
      if (c.type === CALL_TYPE.INCOMING) incoming++;
      else if (c.type === CALL_TYPE.OUTGOING) outgoing++;
      else if (c.type === CALL_TYPE.MISSED) missed++;
      duration += c.duration || 0;
    }
    return { incoming, outgoing, missed, duration };
  }, [calls]);

  const shownCalls = useMemo(() => {
    if (tileFilter === 'all') return calls;
    const want =
      tileFilter === 'in' ? CALL_TYPE.INCOMING :
      tileFilter === 'out' ? CALL_TYPE.OUTGOING :
      CALL_TYPE.MISSED;
    return calls.filter(c => c.type === want);
  }, [calls, tileFilter]);

  const companies = mappedCompanies(hit);
  // Cleaned for display, storage and the service-call / complaint sheets.
  const display = displayNumber(calls[0]?.number || number);
  const name = hit?.contact_person || calls[0]?.cachedName || null;
  const digits = normalizeNumber(display);
  const lastMissed = calls[0]?.type === CALL_TYPE.MISSED;

  return (
    <div className="pb-20">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="flex items-center gap-2 px-3 py-2">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 text-gray-500" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold truncate ${
              name ? 'text-gray-900' : 'text-gray-400 italic'
            }`}>
              {name || 'Unknown number'}
            </div>
            <div className="text-xs text-gray-400 truncate">{display}</div>
          </div>
          <button
            onClick={loadCalls}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-50"
            aria-label="Reload history"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {lastMissed && (
        <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-red-50 border-l-[3px] border-red-500 text-xs text-red-700 font-medium">
          Last call was missed — callback owed
        </div>
      )}

      {/* Primary actions */}
      <div className="flex gap-2 px-3 pt-3">
        <a
          href={`tel:${display}`}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium"
        >
          <Phone className="h-4 w-4" /> Call
        </a>
        <a
          href={`https://wa.me/${digits.length === 10 ? '91' : ''}${digits}`}
          target="_blank" rel="noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
      </div>

      {/* Log something against this call, while the number is still in front of you */}
      <div className="flex gap-2 px-3 pt-2 pb-3">
        <button
          onClick={() => setLogKind('service')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium"
        >
          <Wrench className="h-4 w-4" /> Service call
        </button>
        <button
          onClick={() => setLogKind('complain')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium"
        >
          <AlertCircle className="h-4 w-4" /> Complaint
        </button>
      </div>

      {/* Every customer this number is mapped to — one button each. A number
          shared across companies (a shared office line, an accountant acting for
          several parties) is common, so all of them are offered rather than
          silently picking the first. */}
      <div className="px-3">
        <div className="text-xs font-semibold text-gray-400 mb-1.5">
          {companies.length > 1
            ? `Mapped companies (${companies.length})`
            : 'Mapped company'}
        </div>

        {companies.length === 0 ? (
          <button
            onClick={() => navigate('/call-log', { state: { addNumber: display } })}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-blue-300 text-blue-600 text-sm"
          >
            <UserPlus className="h-4 w-4" />
            Not in ABS Cloud — add to a customer
          </button>
        ) : (
          <div className="space-y-1.5">
            {companies.map(c => (
              <button
                key={c.customer_id}
                onClick={() => navigate('/search', { state: { customerId: c.customer_id } })}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-left"
              >
                <Building2 className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-gray-900 truncate">{c.company}</span>
                  {c.person && (
                    <span className="block text-xs text-gray-400 truncate">{c.person}</span>
                  )}
                </span>
                {c.primary && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Totals — each tile filters the history below. Tapping the active one
          again clears it, so there is always a way back to everything without
          hunting for a separate "clear" control. */}
      <div className="grid grid-cols-4 gap-2 px-3 py-4">
        {([
          { key: 'all', label: 'Total', value: calls.length, tone: 'text-gray-900', ring: 'ring-gray-400' },
          { key: 'in', label: 'In', value: stats.incoming, tone: 'text-emerald-600', ring: 'ring-emerald-500' },
          { key: 'out', label: 'Out', value: stats.outgoing, tone: 'text-blue-600', ring: 'ring-blue-500' },
          { key: 'missed', label: 'Missed', value: stats.missed, tone: 'text-red-600', ring: 'ring-red-500' },
        ] as const).map(s => {
          const active = tileFilter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setTileFilter(active && s.key !== 'all' ? 'all' : s.key)}
              // A zero tile would filter to an empty list — nothing to look at.
              disabled={s.value === 0 && s.key !== 'all'}
              className={`text-center py-2 rounded-lg transition-shadow disabled:opacity-40 ${
                active ? `bg-white ring-2 ${s.ring}` : 'bg-gray-50'
              }`}
            >
              <div className={`text-base font-semibold ${s.tone}`}>{s.value}</div>
              <div className="text-[11px] text-gray-400">{s.label}</div>
            </button>
          );
        })}
      </div>

      {stats.duration > 0 && (
        <div className="px-3 -mt-2 pb-2 text-xs text-gray-400">
          Total talk time: {fmtDuration(stats.duration)}
        </div>
      )}

      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50">
        <span className="text-xs font-semibold text-gray-400">
          Call history
          {tileFilter !== 'all' && (
            <span className="ml-1 font-normal">
              &middot; {tileFilter === 'in' ? 'incoming' : tileFilter === 'out' ? 'outgoing' : 'missed'} only
            </span>
          )}
        </span>
        {tileFilter !== 'all' && (
          <button onClick={() => setTileFilter('all')} className="text-xs text-blue-600">
            Show all
          </button>
        )}
      </div>

      {loading && <div className="p-6 text-center text-sm text-gray-400">Reading call log...</div>}

      {!loading && calls.length === 0 && (
        <div className="p-8 text-center text-sm text-gray-400">
          No calls found for this number.
        </div>
      )}

      {shownCalls.map((c, i) => (
        <div
          key={`${c.date}-${i}`}
          className={`flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 ${
            c.type === CALL_TYPE.MISSED ? 'bg-red-50' : ''
          }`}
        >
          {callIcon(c.type)}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-gray-700">{fmtWhen(c.date)}</div>
          </div>
          <div className="text-xs text-gray-400 shrink-0">
            {fmtDuration(c.duration) || (c.type === CALL_TYPE.MISSED ? 'missed' : 'no answer')}
          </div>
        </div>
      ))}

      {logKind && (
        <LogSheet
          kind={logKind}
          number={display}
          person={name}
          customerId={hit?.customer_id ?? null}
          onClose={() => setLogKind(null)}
          onDone={() => {
            const wasService = logKind === 'service';
            setLogKind(null);
            showSuccess(
              wasService ? 'Service call logged' : 'Complaint logged',
              wasService
                ? 'It now appears in Service Calls.'
                : 'It now appears in the Complain Report.'
            );
          }}
        />
      )}
    </div>
  );
};

export default CallDetail;
